import { GetterTree } from 'vuex'

import { Option, OptionContext } from '@/models'

import { orderByLocale } from '@/utils'

import { RootState } from '../../state'
import { MenuState } from '../state'

const getters: GetterTree<MenuState, RootState> = {
  /**
   * Returns an Option by its context, or null if not found.
   *
   * It throws if the Option is not loaded but its id is in an OptionGroup relation.
   *
   * @param context OptionContext to be found.
   */
  optionByContext: (state) => ({ id, optionGroupId }: OptionContext): Option | null => {
    const optionGroupOptions = state.optionGroups.options[optionGroupId]

    if (optionGroupOptions && optionGroupOptions.ids.has(id)) {
      const option = state.options.elements.find((i) => i.id === id)

      if (option === undefined) {
        throw new Error(`Option "${id}" was not found, but it is in the OptionGroup "${optionGroupId}" relation`)
      }

      return option
    }

    return null
  },

  /**
   * Returns all Options in an OptionGroup.
   *
   * It throws if the Option is not loaded but its id is in an OptionGroup relation.
   *
   * @param optionGroupId OptionGroup id to search.
   */
  optionsByOptionGroupId: (state) => (optionGroupId: number): Option[] => {
    const optionGroupOptions = state.optionGroups.options[optionGroupId]

    if (optionGroupOptions === undefined) {
      return []
    }

    const options: Option[] = []

    for (const id of optionGroupOptions.ids) {
      const option = state.options.elements.find((i) => i.id === id)

      if (option === undefined) {
        throw new Error(`Option "${id}" was not found, but it is in the OptionGroup "${optionGroupId}" relation`)
      }

      options.push(option)
    }

    return options
  },

  /**
   * Returns Options in an OptionGroup ordered by name.
   *
   * @param optionGroupId OptionGroup id to search.
   */
  orderedOptions: (_, getters) => (optionGroupId: number): Option[] => {
    return orderByLocale(getters.optionsByOptionGroupId(optionGroupId), 'name')
  }
}

export default getters
